import { useQuery } from '@tanstack/react-query';
import { env } from '../utils/env';
import * as Sentry from '@sentry/react';
import { Transaction, TransactionType, PaginatedTransactionResponse } from '../types/transaction';

interface UseTransactionsOptions {
    isVisible?: boolean;
    startDate?: Date;
    endDate?: Date;
    search?: string;
    categoryIds?: string[];
    showUncategorized?: boolean;
    showIncomes?: boolean;
    page?: number;
    pageSize?: number;
}

const formatDate = (date: Date | undefined): string | undefined => {
    if (!date) return undefined;
    // return date.toISOString().split('T')[0]; // YYYY-MM-DD
    // Ensure date is treated as local timezone before formatting to avoid off-by-one day errors
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export function useTransactions(accessToken: string, options: UseTransactionsOptions = {}) {
    const {
        isVisible = true,
        startDate,
        endDate,
        search,
        categoryIds,
        showUncategorized = false,
        showIncomes = false,
        page = 1,
        pageSize = 50
    } = options;

    const formattedStartDate = formatDate(startDate);
    const formattedEndDate = formatDate(endDate);

    const result = useQuery({
        queryKey: [
            'transactions',
            formattedStartDate,
            formattedEndDate,
            search,
            categoryIds?.join(','),
            showUncategorized,
            showIncomes,
            page,
            pageSize
        ],
        queryFn: async (): Promise<PaginatedTransactionResponse> => {
            try {
                const params = new URLSearchParams();

                if (formattedStartDate) {
                    params.append('start_date', formattedStartDate);
                }
                if (formattedEndDate) {
                    params.append('end_date', formattedEndDate);
                }
                if (search) {
                    params.append('search', search);
                }
                if (categoryIds && categoryIds.length > 0) {
                    categoryIds.forEach(id => params.append('category_id', id));
                }
                if (showUncategorized) {
                    params.append('uncategorized', 'true');
                }
                if (showIncomes) {
                    params.append('transaction_type', TransactionType.INCOME);
                }
                params.append('page', page.toString());
                params.append('size', pageSize.toString());

                const response = await fetch(`${env.API_URL}/api/transactions?${params.toString()}`, {
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                    },
                    credentials: 'include',
                });

                if (!response.ok) {
                    throw new Error(`Failed to fetch transactions: ${response.status}`);
                }
                return response.json() as Promise<PaginatedTransactionResponse>;
            } catch (error) {
                console.error('Error fetching transactions:', error);
                Sentry.captureException(error, {
                    tags: {
                        component: 'useTransactions',
                        action: 'fetchTransactions'
                    },
                    extra: {
                        accessToken: accessToken ? 'present' : 'missing',
                        options: JSON.stringify(options)
                    }
                });
                throw error;
            }
        },
        enabled: isVisible && !!accessToken,
    });

    return {
        data: result.data,
        transactions: result.data?.items || [],
        totalPages: result.data?.pages || 0,
        totalItems: result.data?.total || 0,
        isLoading: result.isLoading,
        error: result.error ? (result.error instanceof Error ? result.error.message : String(result.error)) : null,
        refetch: result.refetch
    };
} 